import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { api, FRANJAS, getCurrentFranja, getUser } from '../api.js';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const FRANJAS_TABLA = FRANJAS; // M T N A D

// Plantilla precargable del formato físico (4-10 mayo 2026)
const PLANTILLA_4_10_MAYO = {
  '2026-05-04': { M: 'Betsy',   T: 'Viviana', N: 'Marcela', A: 'Nancy',   D: 'Iveth'   },
  '2026-05-05': { M: 'Viviana', T: 'Iveth',   N: 'Betsy',   A: 'Nancy',   D: 'Marcela' },
  '2026-05-06': { M: 'Iveth',   T: 'Marcela', N: 'Betsy',   A: 'Nancy',   D: 'Viviana' },
  '2026-05-07': { M: 'Iveth',   T: 'Marcela', N: 'Viviana', A: 'Betsy',   D: 'Nancy'   },
  '2026-05-08': { M: 'Marcela', T: 'Nancy',   N: 'Iveth',   A: 'Viviana', D: 'Betsy'   },
  '2026-05-09': { M: 'Nancy',   T: 'Betsy',   N: 'Marcela', A: 'Iveth',   D: 'Viviana' },
  '2026-05-10': { M: 'Nancy',   T: 'Betsy',   N: 'Iveth',   A: 'Marcela', D: 'Viviana' },
};

function lunesDe(fechaStr) {
  const d = new Date(fechaStr + 'T00:00:00');
  const dow = d.getDay(); // 0=dom
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function sumarDias(fechaStr, n) {
  const d = new Date(fechaStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDiaCorto(fechaStr) {
  const d = new Date(fechaStr + 'T00:00:00');
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
}

export default function Horario() {
  const user = getUser();
  const esAdmin = user?.rol === 'admin';
  const hoyStr = new Date().toISOString().slice(0, 10);
  const [desde, setDesde] = useState(lunesDe(hoyStr));
  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => sumarDias(desde, i)), [desde]);
  const [usuarios, setUsuarios] = useState([]);
  const [asigPorCelda, setAsigPorCelda] = useState({}); // key `${fecha}|${franja}` → usuario_id
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const actual = getCurrentFranja();

  const cargar = async () => {
    setLoading(true);
    setMensaje('');
    try {
      const [u, h] = await Promise.all([api.usuarios(), api.horarioSemana(desde, 7)]);
      const trabajadoras = (u.usuarios ?? []).filter((x) => x.activo !== 0 && x.rol !== 'admin' && x.rol !== 'contador');
      setUsuarios(trabajadoras);
      const map = {};
      for (const a of h.asignaciones ?? []) {
        map[`${a.fecha}|${a.franja}`] = a.usuario_id;
      }
      setAsigPorCelda(map);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [desde]);

  const setCelda = (fecha, franja, usuario_id) => {
    setAsigPorCelda((prev) => ({ ...prev, [`${fecha}|${franja}`]: usuario_id ? Number(usuario_id) : null }));
  };

  const guardar = async () => {
    setSaving(true);
    setMensaje('');
    try {
      const asignaciones = [];
      for (const fecha of dias) {
        for (const f of FRANJAS_TABLA) {
          const id = asigPorCelda[`${fecha}|${f.codigo}`] ?? null;
          asignaciones.push({ fecha, franja: f.codigo, usuario_id: id });
        }
      }
      await api.guardarHorario(asignaciones);
      setMensaje('Horario guardado.');
    } catch (e) {
      setMensaje('No se pudo guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const cargarPlantillaMayo = () => {
    const next = { ...asigPorCelda };
    for (const [fecha, franjas] of Object.entries(PLANTILLA_4_10_MAYO)) {
      for (const [franja, nombre] of Object.entries(franjas)) {
        const u = usuarios.find((x) => x.nombre.toLowerCase().startsWith(nombre.toLowerCase()) ||
                                     nombre.toLowerCase().startsWith(x.nombre.toLowerCase().split(' ')[0]));
        if (u) next[`${fecha}|${franja}`] = u.id;
      }
    }
    setAsigPorCelda(next);
    setMensaje('Plantilla cargada. Recuerda guardar.');
  };

  const navSemana = (delta) => setDesde((d) => sumarDias(d, delta * 7));

  return (
    <Layout>
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="eyebrow mb-2">Horario</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Turnos de la semana</h1>
          <p className="text-muted mt-2 max-w-xl">
            Cada celda muestra quién entra a ese turno. Cuando alguien abre turno desde Login, el sistema sabe quién le toca.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navSemana(-1)} className="btn-ghost">← Anterior</button>
          <div className="pill-ink">Semana del {fmtDiaCorto(desde)} a {fmtDiaCorto(dias[6])}</div>
          <button onClick={() => navSemana(1)} className="btn-ghost">Siguiente →</button>
          <button onClick={() => setDesde(lunesDe(hoyStr))} className="btn-ghost text-muted">Hoy</button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted border-b border-line">Día</th>
              {FRANJAS_TABLA.map((f) => (
                <th key={f.codigo} className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted border-b border-line">
                  <div className="text-ink font-semibold normal-case tracking-tight text-sm">{f.short}</div>
                  <div className="font-mono text-[10px] text-muted mt-0.5">{f.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dias.map((fecha, i) => {
              const esHoy = fecha === actual.fecha;
              return (
                <tr key={fecha} className={esHoy ? 'bg-bone/40' : ''}>
                  <td className="p-3 align-top border-b border-line/60">
                    <div className="font-semibold tracking-tight">{DIAS_SEMANA[i]}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-1">{fmtDiaCorto(fecha)}</div>
                    {esHoy && <div className="pill-lime mt-2">Hoy</div>}
                  </td>
                  {FRANJAS_TABLA.map((f) => {
                    const key = `${fecha}|${f.codigo}`;
                    const id = asigPorCelda[key] ?? '';
                    const esActual = esHoy && f.codigo === actual.franja;
                    return (
                      <td key={f.codigo} className={`p-2 align-top border-b border-line/60 ${esActual ? 'bg-lime/30' : ''}`} style={esActual ? { background: 'rgba(200,232,71,0.25)' } : null}>
                        {esAdmin ? (
                          <select
                            className="input"
                            value={id}
                            onChange={(e) => setCelda(fecha, f.codigo, e.target.value)}
                          >
                            <option value="">—</option>
                            {usuarios.map((u) => (
                              <option key={u.id} value={u.id}>{u.nombre}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="px-3 py-2 rounded-2xl bg-bone/60 font-medium tracking-tight min-h-[44px] flex items-center">
                            {usuarios.find((u) => u.id === id)?.nombre ?? <span className="text-muted">—</span>}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {esAdmin && (
        <div className="flex items-center justify-between gap-3 mt-6 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={guardar} disabled={saving || loading} className="btn-primary">
              {saving ? 'Guardando…' : 'Guardar horario'}
            </button>
            <button onClick={cargar} disabled={loading} className="btn-ghost">Recargar</button>
            {desde === '2026-05-04' && (
              <button onClick={cargarPlantillaMayo} className="btn-secondary">
                Cargar plantilla 4–10 mayo
              </button>
            )}
          </div>
          {mensaje && <div className="font-mono text-xs text-muted">{mensaje}</div>}
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <div className="card-flat">
          <p className="eyebrow mb-2">Cómo funciona</p>
          <ul className="text-sm space-y-2 text-ink/80">
            <li>· La fila iluminada es <b>hoy</b>. La celda iluminada en lima es la franja <b>en curso</b>.</li>
            <li>· Cuando una trabajadora entra al sistema, su nombre se sugiere automáticamente según el turno actual.</li>
            <li>· Al abrir el turno, queda registrado con la franja (M / T / N / A) en los reportes.</li>
          </ul>
        </div>
        <div className="card-flat">
          <p className="eyebrow mb-2">Franjas</p>
          <ul className="text-sm space-y-2 text-ink/80 font-mono">
            {FRANJAS.map((f) => (
              <li key={f.codigo}><b>{f.codigo}</b> · {f.short} · {f.label}</li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
}
